import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AssessmentService from "src/services/assessment/assessmentService";
import AssessmentDisplayDataService from "src/services/assessment/assessmentDisplayDataService";
import AssessmentFilterService from "src/services/assessment/assessmentFilterService";
import { DisplayData } from "src/services/assessment/types/displayData";
import { AssessmentDetailsApiData } from "src/services/assessment/types/assessmentDetailsApiData";
import FilterTagsAndOptionsService from "src/services/assessment/filterTagsAndOptionsService";
import { FilterTag } from "src/services/assessment/types/filterTag";
import { FilterOptions } from "src/services/assessment/types/filterOptions";
import { TabGroupValues } from "src/technicalRiskAssessment/components/pages/assessmentLibrary/tabGroupValues";
import { filterOldErroredAssessmentsByDays } from "src/utils/dateUtil";

const initialFilterOptions: FilterOptions = {
  rebaScoreRanges: [],
  assessmentsAvailable: [],
  videoStatuses: [],
};

export type AssessmentLibraryState = {
  isLoading: boolean;
  searchField: string;
  currentPage: number;
  perPage: number;
  totalPages: number;
  myAssessmentsDisplayData: DisplayData[];
  allAssessmentsDisplayData: DisplayData[];
  siteAssessmentsDisplayData: DisplayData[];
  allAssessments: AssessmentDetailsApiData[];
  myAssessments: AssessmentDetailsApiData[];
  siteAssessments: AssessmentDetailsApiData[];
  processingAssessmentsDisplayData: DisplayData[];
  currentTab: string;
  isAdvancedFilterSheetOpen: boolean;
  selectedFilterOptions: FilterOptions;
  appliedFilterOptions: FilterOptions;
  filterTags: FilterTag[];
};

const initialState: AssessmentLibraryState = {
  isLoading: false,
  searchField: "",
  currentPage: 1,
  perPage: 10,
  totalPages: 0,
  myAssessmentsDisplayData: [],
  allAssessmentsDisplayData: [],
  siteAssessmentsDisplayData: [],
  allAssessments: [],
  myAssessments: [],
  siteAssessments: [],
  processingAssessmentsDisplayData: [],
  currentTab: TabGroupValues.SITE_ASSESSMENTS,
  isAdvancedFilterSheetOpen: false,
  selectedFilterOptions: initialFilterOptions,
  appliedFilterOptions: initialFilterOptions,
  filterTags: [],
};

interface OwnerAndClientId {
  owner: string;
  clientId: string;
}

interface OwnerClientSite extends OwnerAndClientId {
  site: string;
}

/**
 * get all assessments
 * @returns {Object}
 */
export const getAllAssessments = createAsyncThunk(
  "assessments/getAssessmentDetailsByClientId",
  async ({ owner, clientId }: OwnerAndClientId) => {
    const { data } = await AssessmentService.getAllAssessmentsByClientId(
      clientId,
      owner
    );
    return data;
  }
);

export const getMyAssessments = createAsyncThunk(
  "assessments/getAssessmentsDetailsByOwner",
  async ({ owner, clientId, site }: OwnerClientSite) => {
    const { data } = await AssessmentService.getMyAssessments(clientId, owner);
    if (site.length > 0) {
      const assessmentItems = data.assessmentDetails.filter(
        (item: AssessmentDetailsApiData) => item.site === site
      );
      data.assessmentDetails = assessmentItems;
    }
    return data;
  }
);

export const getSiteAssessments = createAsyncThunk(
  "assessments/getAssessmentDetailsByFilters",
  async ({ owner, clientId, site }: OwnerClientSite) => {
    const { data } = await AssessmentService.getSiteAssessments(
      clientId,
      owner,
      site
    );
    return data;
  }
);

/** Assessment Library Slice */
const { reducer, actions } = createSlice({
  name: "assessmentLibrarySlice",
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setSearchField: (state, action) => {
      state.searchField = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setCurrentTab: (state, action) => {
      state.currentTab = action.payload;
    },
    setIsAdvancedFilterSheetOpen: (state, action) => {
      state.isAdvancedFilterSheetOpen = action.payload;
    },
    setSelectedFilterOptions: (state, action) => {
      state.selectedFilterOptions = action.payload;
    },
    resetFilters: (state) => {
      state.selectedFilterOptions = initialFilterOptions;
    },
    applyFilters: (state) => {
      state.appliedFilterOptions = state.selectedFilterOptions;
      state.allAssessmentsDisplayData = extractAndFilterAssessments(
        state.allAssessments,
        state.appliedFilterOptions
      );
      state.myAssessmentsDisplayData = extractAndFilterAssessments(
        state.myAssessments,
        state.appliedFilterOptions
      );
      state.siteAssessmentsDisplayData = extractAndFilterAssessments(
        state.siteAssessments,
        state.appliedFilterOptions
      );
      state.processingAssessmentsDisplayData = filterProcessingFromDisplayData(
        state.myAssessmentsDisplayData
      );

      state.filterTags = FilterTagsAndOptionsService.createFilterTags(
        state.appliedFilterOptions
      );
      state.currentPage = 1;
    },
    removeTagAndUpdateFilters: (state, action) => {
      state.filterTags = FilterTagsAndOptionsService.removeFilterTag(
        state.filterTags,
        action.payload
      );

      state.appliedFilterOptions =
        FilterTagsAndOptionsService.getUpdatedAppliedFilterOptionsOnTagRemoval(
          state.appliedFilterOptions,
          action.payload.optionName,
          action.payload.value
        );
      state.selectedFilterOptions = state.appliedFilterOptions;
      state.allAssessmentsDisplayData = extractAndFilterAssessments(
        state.allAssessments,
        state.appliedFilterOptions
      );
      state.myAssessmentsDisplayData = extractAndFilterAssessments(
        state.myAssessments,
        state.appliedFilterOptions
      );
      state.siteAssessmentsDisplayData = extractAndFilterAssessments(
        state.siteAssessments,
        state.appliedFilterOptions
      );
      state.processingAssessmentsDisplayData = filterProcessingFromDisplayData(
        state.myAssessmentsDisplayData
      );
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllAssessments.fulfilled, (state, { payload }) => {
      const filteredData = filterOldErroredAssessmentsByDays(
        payload.assessmentDetails
      );
      state.allAssessments = filteredData;
      state.allAssessmentsDisplayData = extractAndFilterAssessments(
        filteredData,
        state.appliedFilterOptions
      );
    });

    builder.addCase(getMyAssessments.fulfilled, (state, { payload }) => {
      const filteredData = filterOldErroredAssessmentsByDays(
        payload.assessmentDetails
      );
      state.myAssessments = filteredData;
      state.myAssessmentsDisplayData = extractAndFilterAssessments(
        filteredData,
        state.appliedFilterOptions
      );
      state.processingAssessmentsDisplayData = filterProcessingFromDisplayData(
        state.myAssessmentsDisplayData
      );
    });

    builder.addCase(getSiteAssessments.fulfilled, (state, { payload }) => {
      const filteredData = filterOldErroredAssessmentsByDays(
        payload.assessmentDetails
      );
      state.siteAssessments = filteredData;
      state.siteAssessmentsDisplayData = extractAndFilterAssessments(
        filteredData,
        state.appliedFilterOptions
      );
      state.processingAssessmentsDisplayData = filterProcessingFromDisplayData(
        state.siteAssessmentsDisplayData
      );
    });
  },
});

const extractAndFilterAssessments = (
  assessments: AssessmentDetailsApiData[],
  filterOptions: FilterOptions
) => {
  const assessmentDisplayDataArray = assessments.map(
    (assessment: AssessmentDetailsApiData) =>
      AssessmentDisplayDataService.assessmentsApiDataToDisplayData(assessment)
  );
  return AssessmentFilterService.filterAssessments(
    assessmentDisplayDataArray,
    filterOptions
  );
};

const filterProcessingFromDisplayData = (assessments: DisplayData[]) => {
  const processingSet = new Set(["Processing", "Uploading"]);
  return assessments.filter((assessment) => {
    return (
      processingSet.has(assessment.initialEvaluation.value) ||
      processingSet.has(assessment.followUpEvaluation.value)
    );
  });
};

export const {
  setIsLoading,
  setSearchField,
  setCurrentPage,
  setPerPage,
  setTotalPages,
  setCurrentTab,
  setIsAdvancedFilterSheetOpen,
  resetFilters,
  setSelectedFilterOptions,
  applyFilters,
  removeTagAndUpdateFilters,
} = actions;

export default reducer;
